import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import type { LeaderboardRow } from '@shared/types';
import { useGame } from '@/store/game';
import { sfx } from '@/audio/sfx';
import { haptic } from '@/lib/haptics';
import { AppHeader } from '@/components/AppHeader';
import { Button } from '@/components/Button';
import { MascotSlot } from '@/brand/MascotSlot';
import { ConfettiBurst, Wash } from '@/motion';
import { Screen } from './parts/Screen';
import { PlaceLine, StatGrid } from './parts/StatGrid';
import { COPY } from './parts/copy';
import { useOwnSnapshot, isFinish } from './parts/frozen';
import { BEATS } from './parts/moments';
import { FINISH, asksForDetails, finishFacts, finishLine, finishStats } from './parts/finish';
import { useCalm } from './parts/time';
import { activePrize, termsPath } from './parts/prize';
import { detailsAsked, detailsGiven, loadMe, resultPath } from '@/lib/session';
import { returnPath } from '@/lib/proof';
import { QUALIFY } from './parts/qualify';

type Stage = 0 | 1 | 2 | 3 | 4;

/** The podium's extras at the first-place beat: a longer volley from both sides, a wash in the brand. The drumroll and fanfare keep their beats (BEATS). */
export const PODIUM_FX = { confetti: 220, confettiLife: [3, 4.4] as [number, number], staggerMs: 1400, wash: 1200 } as const;

const PLACE = {
  1: { h: 'h-40', block: 'bg-brand text-ink-inverse', name: 'text-ink' },
  2: { h: 'h-28', block: 'bg-ink text-ink-inverse', name: 'text-ink' },
  3: { h: 'h-20', block: 'bg-surface-2 text-ink', name: 'text-ink' },
} as const;

function Step({ row, place, show, reduce, mine }: { row?: LeaderboardRow; place: 1 | 2 | 3; show: boolean; reduce: boolean; mine?: string }) {
  if (!row) return <li className="w-1/3" aria-hidden="true" />;
  const look = PLACE[place];
  return (
    <motion.li data-place={place} className="flex w-1/3 flex-col items-center justify-end gap-1.5"
      initial={false} animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 60 }}
      transition={reduce ? { duration: 0.2 } : { type: 'spring', stiffness: 260, damping: 17 }}>
      {place === 1 && <MascotSlot moment="podium" size={84} />}
      {/* First place's name lands with a bounce; the others simply rise with their step. */}
      <motion.span initial={false} animate={{ scale: place === 1 && !reduce && !show ? 0.4 : 1 }} transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 13, delay: 0.12 }}
        data-part="podium-name" className={`w-full truncate text-center font-bold ${place === 1 ? 'text-xl' : ''} ${look.name}`}>{row.name}{row.playerId === mine && <span className="sr-only"> {FINISH.isYou}</span>}</motion.span>
      <span data-part="podium-score" className="numeral text-lg text-ink-2">{row.score.toLocaleString('en-GB')}</span>
      <div data-part="podium-plinth" className={`flex w-full items-start justify-center rounded-t-tile pt-2 ${look.h} ${look.block}`}>
        <span className="numeral text-5xl leading-none">{place}</span>
      </div>
    </motion.li>
  );
}

/**
 * M10: the podium. The stage dims, a drumroll, third and second rise, a pause, first with the crown, then your result card.
 * Each fact once: on the podium your step carries your place and points, so your card adds only what the step does not.
 */
export default function Podium() {
  const snap = useOwnSnapshot(isFinish); const me = useGame((s) => s.me); const session = useGame((s) => s.session);
  const nav = useNavigate();
  const reduce = Boolean(useCalm());
  const [stage, setStage] = useState<Stage>(reduce ? 4 : 0);
  const podium = snap?.podium ?? [];
  const byPlace = (p: number) => podium.find((r) => r.rank === p) ?? podium[p - 1];

  useEffect(() => {
    if (reduce) { setStage(4); return; }
    sfx.play('drumroll');
    const ts = [
      window.setTimeout(() => setStage(1), BEATS.third),
      window.setTimeout(() => setStage(2), BEATS.second),
      window.setTimeout(() => { setStage(3); sfx.play('fanfare'); haptic('podium'); }, BEATS.first),
      window.setTimeout(() => setStage(4), BEATS.card),
    ];
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, [reduce]);

  const mine = me && session && me.playerId === session.playerId ? me : undefined;
  const facts = mine ? finishFacts(mine, snap?.top ?? snap?.podium) : undefined;
  const line = facts ? finishLine(facts) : undefined;
  const onPodium = Boolean(mine && podium.some((r) => r.playerId === mine.playerId));
  const answered = !facts || facts.answered > 0;
  // R1: the result page opens on the read-only link GET /me handed this phone, never on the session; until that has
  // arrived, on /r, which takes this phone's session.
  const resultLink = mine?.resultToken ? resultPath(mine.resultToken) : session?.token ? '/r' : undefined;
  // 9 Oct: the details step comes first, once, when the battle asks and the player has nothing saved (parts/finish). Its
  // address carries the session (lib/proof returnPath), so a reload or a killed WebView on the step keeps its place.
  // 14 Oct: and not when this phone already gave them for this address today (lib/session detailsGiven).
  const next = resultLink && mine && session && asksForDetails(mine) && !detailsAsked(session.code, session.playerId) && !detailsGiven(loadMe()?.email) ? returnPath(session, 'details') : resultLink;
  const dim = stage > 0 && stage < 3;
  const prize = activePrize(snap?.prize);
  const code = session?.code ?? snap?.code;

  return (
    <div className="relative min-h-full">
      <motion.div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0"
        style={{ background: 'radial-gradient(ellipse 80% 55% at 50% 38%, transparent 0%, color-mix(in srgb, var(--ink) 28%, transparent) 100%)' }}
        initial={{ opacity: 0 }} animate={{ opacity: dim ? 1 : 0 }} transition={{ duration: dim ? 0.5 : 0.35 }} />
      <Screen phase="podium" className="relative z-10" header={<AppHeader />}>
        <h1 className="display pt-1 text-center text-[2.75rem] text-ink">{FINISH.podium}</h1>
        {snap?.qualify && <p data-part="qualify-rule" className="text-center font-semibold text-brand-strong">{QUALIFY.rule(snap.qualify)}</p>}

        <ol aria-label={FINISH.topThree} data-part="podium-steps" className="flex w-full items-end justify-center gap-2.5 pt-4">
          <Step row={byPlace(2)} place={2} show={stage >= 2} reduce={reduce} mine={mine?.playerId} />
          <Step row={byPlace(1)} place={1} show={stage >= 3} reduce={reduce} mine={mine?.playerId} />
          <Step row={byPlace(3)} place={3} show={stage >= 1} reduce={reduce} mine={mine?.playerId} />
        </ol>

        <motion.section aria-live="polite" aria-labelledby="your-result" initial={false} animate={stage >= 4 ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 80 }}
          transition={reduce ? { duration: 0.2 } : { type: 'spring', stiffness: 220, damping: 24 }}
          className="flex flex-col gap-3 rounded-card bg-surface p-5 shadow-pop">
          <h2 id="your-result" className="font-display text-xl font-extrabold text-ink">{FINISH.you}</h2>
          {facts ? (
            <>
              {!onPodium && facts.rank !== undefined && <PlaceLine rank={facts.rank} players={facts.players} />}
              {line && <p className="text-lg font-semibold leading-snug text-brand-strong">{line}</p>}
              <StatGrid items={finishStats(facts, { points: !onPodium })} />
            </>
          ) : (
            <p className="text-ink-2">{FINISH.loading}</p>
          )}
          {/* On to the result page with every question explained, by the details step when it asks (screens/Details). */}
          {next && <Button onClick={() => nav(next)}>{answered ? FINISH.review : FINISH.reviewQuestions}</Button>}
        </motion.section>

        {prize && code && (
          // v2.1: the promise the prize terms make, with the terms one tap away.
          <motion.p initial={false} animate={stage >= 4 ? { opacity: 1 } : { opacity: 0 }} transition={{ duration: 0.3, delay: reduce ? 0 : 0.2 }}
            className="text-center text-sm text-ink-2">
            {COPY.prize.emailed}{' '}
            <a href={termsPath(code)} target="_blank" rel="noopener" className="py-3 font-semibold text-iv-strong underline underline-offset-2">{COPY.prize.termsApply}</a>
          </motion.p>
        )}
      </Screen>
      {stage >= 3 && (
        <>
          <Wash trigger={`podium-${snap?.code}`} color="var(--brand)" peak={0.28} durationMs={PODIUM_FX.wash} />
          <ConfettiBurst trigger={`podium-${snap?.code}`} origin="sides" count={PODIUM_FX.confetti} life={PODIUM_FX.confettiLife} staggerMs={PODIUM_FX.staggerMs} />
        </>
      )}
    </div>
  );
}
